/**
 * 계정 정보 카드
 * 데이터베이스 기반 실제 사용자 정보 표시
 */

import { useState, useEffect } from "react"
import Card from "@mui/material/Card"
import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import { request } from "app/network"
import { UserProfile } from "app/types/UserProfile"
import ChangePasswordDialog from "../ChangePasswordDialog"
import EditProfileDialog from "../EditProfileDialog"

interface AccountInfoProps {
  userProfile?: any // 기존 mock 데이터 (사용 안 함)
}

function AccountInfo({ userProfile: mockProfile }: AccountInfoProps): JSX.Element {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false)

  useEffect(() => {
    loadUserProfile()
  }, [])

  const loadUserProfile = async () => {
    try {
      setLoading(true)
      const response = await request("User.getCurrentUser", { paramz: {} })

      if (response.status === 200 && response.data) {
        const userData = response.data
        // enable 필드를 isActive로 변환
        if (userData.enable !== undefined && userData.isActive === undefined) {
          userData.isActive = userData.enable
        }
        // mobile 필드를 phone으로 변환
        if (userData.mobile !== undefined && userData.phone === undefined) {
          userData.phone = userData.mobile
        }
        setUserProfile(userData)
      }
    } catch (error) {
      console.error("사용자 정보 로드 실패:", error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Card sx={{ height: "100%", boxShadow: "none" }}>
        <MDBox p={2}>
          <MDTypography variant="body2" color="text">
            로딩 중...
          </MDTypography>
        </MDBox>
      </Card>
    )
  }

  if (!userProfile) {
    return (
      <Card sx={{ height: "100%", boxShadow: "none" }}>
        <MDBox p={2}>
          <MDTypography variant="body2" color="error">
            사용자 정보를 불러올 수 없습니다.
          </MDTypography>
        </MDBox>
      </Card>
    )
  }

  return (
    <Card sx={{ height: "100%", boxShadow: "none" }}>
      <MDBox p={2}>
        <MDBox mb={2}>
          <MDTypography variant="h6" fontWeight="medium">
            계정 정보
          </MDTypography>
        </MDBox>

        <Grid container spacing={2}>
          {/* 사용자 ID */}
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="button" fontWeight="bold" color="text">
                사용자 ID
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                {userProfile.id}
              </MDTypography>
            </MDBox>
          </Grid>

          {/* 이름 */}
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="button" fontWeight="bold" color="text">
                이름
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                {userProfile.name || "-"}
              </MDTypography>
            </MDBox>
          </Grid>

          {/* 닉네임 */}
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="button" fontWeight="bold" color="text">
                닉네임
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                {userProfile.nickname || "-"}
              </MDTypography>
            </MDBox>
          </Grid>

          {/* 이메일 */}
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="button" fontWeight="bold" color="text">
                이메일
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                {userProfile.email || "-"}
              </MDTypography>
            </MDBox>
          </Grid>

          {/* 전화번호 */}
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="button" fontWeight="bold" color="text">
                전화번호
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                {userProfile.phone || "-"}
              </MDTypography>
            </MDBox>
          </Grid>

          {/* 로그인 방식 */}
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="button" fontWeight="bold" color="text">
                로그인 방식
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                {userProfile.loginType || "-"}
              </MDTypography>
            </MDBox>
          </Grid>

          {/* 계정 상태 */}
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="button" fontWeight="bold" color="text">
                계정 상태
              </MDTypography>
              <MDTypography
                variant="button"
                fontWeight="regular"
                color={userProfile.isActive ? "success" : "error"}
              >
                {userProfile.isActive ? "활성" : "비활성"}
              </MDTypography>
            </MDBox>
          </Grid>

          {/* 관리자 권한 */}
          {userProfile.isAdmin && (
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="button" fontWeight="bold" color="text">
                  관리자 권한
                </MDTypography>
                <MDTypography variant="button" fontWeight="regular" color="info">
                  예
                </MDTypography>
              </MDBox>
            </Grid>
          )}

          {/* 가입일 */}
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="button" fontWeight="bold" color="text">
                가입일
              </MDTypography>
              <MDTypography variant="button" fontWeight="regular" color="text">
                {userProfile.crtDt
                  ? new Date(userProfile.crtDt).toLocaleDateString("ko-KR")
                  : "-"}
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>

        {/* 프로필 수정 및 비밀번호 변경 버튼 */}
        <MDBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            onClick={() => setEditProfileDialogOpen(true)}
          >
            프로필 수정
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => setPasswordDialogOpen(true)}
          >
            비밀번호 변경
          </MDButton>
        </MDBox>
      </MDBox>

      {/* 프로필 수정 다이얼로그 */}
      <EditProfileDialog
        open={editProfileDialogOpen}
        onClose={() => setEditProfileDialogOpen(false)}
        userProfile={userProfile}
        onProfileUpdated={loadUserProfile}
      />

      {/* 비밀번호 변경 다이얼로그 */}
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
      />
    </Card>
  )
}

export default AccountInfo
